import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApiClient } from "../../providers/AuthProvider";
import { useTenant } from "../../providers/TenantProvider";
import type { EditableSlideshowMediaItem } from "../../../../common/src/types";
import type { PermissionGroup } from "../../components/HierarchyPicker/types";

// --- Types ---

export type Slideshow = {
  id: string;
  name: string;
  color: string;
  items: EditableSlideshowMediaItem[];
  metadata: Record<string, unknown>;
  groupId: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
};

type SlideshowsContextType = {
  // Data
  slideshows: Slideshow[];
  groups: PermissionGroup[];
  loading: boolean;
  error: string | null;

  // Selection
  selectedSlideshowId: string | null;
  selectSlideshow: (id: string | null) => void;

  // CRUD operations
  fetchSlideshows: () => Promise<void>;
  createSlideshow: (data: {
    name: string;
    color?: string;
    items?: EditableSlideshowMediaItem[];
    metadata?: Record<string, unknown>;
    groupId?: string;
  }) => Promise<Slideshow>;
  updateSlideshow: (
    id: string,
    data: {
      name?: string;
      color?: string;
      items?: EditableSlideshowMediaItem[];
      metadata?: Record<string, unknown>;
      groupId?: string;
    },
  ) => Promise<Slideshow>;
  deleteSlideshow: (id: string) => Promise<void>;
};

const SlideshowsContext = createContext<SlideshowsContextType | null>(null);

// --- Provider ---

type Props = {
  children: React.ReactNode;
};

export const SlideshowsProvider = ({ children }: Props) => {
  const apiClient = useApiClient();
  const { currentTenantId } = useTenant();
  const [slideshows, setSlideshows] = useState<Slideshow[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlideshowId, setSelectedSlideshowId] = useState<string | null>(null);

  // --- Fetch slideshows ---

  const fetchSlideshows = useCallback(async () => {
    if (!currentTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: currentTenantId });
      const groupsParams = new URLSearchParams({ tenantId: currentTenantId, permission: "schedules.create" });
      const [slideshowsRes, groupsRes] = await Promise.all([
        apiClient.get<{ slideshows: Slideshow[] }>(`/api/slideshows?${params}`),
        apiClient.get<{ groups: PermissionGroup[] }>(`/api/groups?${groupsParams}`),
      ]);
      setSlideshows(slideshowsRes.slideshows.map((s) => ({ ...s, items: s.items ?? [], metadata: s.metadata ?? {} })));
      setGroups(groupsRes.groups);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiClient, currentTenantId]);

  // --- Create slideshow ---

  const createSlideshow = useCallback(
    async (data: {
      name: string;
      color?: string;
      items?: EditableSlideshowMediaItem[];
      metadata?: Record<string, unknown>;
      groupId?: string;
    }): Promise<Slideshow> => {
      if (!currentTenantId) throw new Error("No tenant selected");
      const result = await apiClient.post<{ slideshow: Slideshow }>("/api/slideshows", {
        ...data,
        groupId: data.groupId ?? currentTenantId,
      });
      const slideshow = {
        ...result.slideshow,
        items: result.slideshow.items ?? [],
        metadata: result.slideshow.metadata ?? {},
      };
      setSlideshows((prev) => [...prev, slideshow]);
      return slideshow;
    },
    [apiClient, currentTenantId],
  );

  // --- Update slideshow ---

  const updateSlideshow = useCallback(
    async (
      id: string,
      data: {
        name?: string;
        color?: string;
        items?: EditableSlideshowMediaItem[];
        metadata?: Record<string, unknown>;
        groupId?: string;
      },
    ): Promise<Slideshow> => {
      const result = await apiClient.put<{ slideshow: Slideshow }>(`/api/slideshows/${id}`, data);
      const slideshow = {
        ...result.slideshow,
        items: result.slideshow.items ?? [],
        metadata: result.slideshow.metadata ?? {},
      };
      setSlideshows((prev) => prev.map((s) => (s.id === id ? slideshow : s)));
      return slideshow;
    },
    [apiClient],
  );

  // --- Delete slideshow ---

  const deleteSlideshow = useCallback(
    async (id: string): Promise<void> => {
      await apiClient.delete(`/api/slideshows/${id}`);
      setSlideshows((prev) => prev.filter((s) => s.id !== id));
      setSelectedSlideshowId((current) => (current === id ? null : current));
    },
    [apiClient],
  );

  // --- Selection ---

  const selectSlideshow = useCallback((id: string | null) => {
    setSelectedSlideshowId(id);
  }, []);

  // --- Load on mount ---

  useEffect(() => {
    fetchSlideshows();
  }, [fetchSlideshows]);

  // --- Context value ---

  const value: SlideshowsContextType = {
    slideshows,
    groups,
    loading,
    error,
    selectedSlideshowId,
    selectSlideshow,
    fetchSlideshows,
    createSlideshow,
    updateSlideshow,
    deleteSlideshow,
  };

  return <SlideshowsContext.Provider value={value}>{children}</SlideshowsContext.Provider>;
};

// --- Hook ---

export const useSlideshows = (): SlideshowsContextType => {
  const context = useContext(SlideshowsContext);
  if (!context) {
    throw new Error("useSlideshows must be used within a SlideshowsProvider");
  }
  return context;
};
